import React, {useEffect, useMemo, useState} from "react";
import {Sidebar} from "primereact/sidebar";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputTextarea} from "primereact/inputtextarea";
import {Checkbox} from "primereact/checkbox";
import Icon from "../../../system/components/icon";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {
    RISK_STATUS,
    SEVERITY,
    TASK_STATUS,
    attentionTitle,
    daysText,
    fullDate,
    shortDate,
    signalText,
    signalsSummary,
} from "./dashboard-labels";

// Drawers and dialogs of the project dashboard: «چرا در خطر است؟», «نیازمند اقدام», «برنامه زمانی پروژه»,
// «ایجاد پیگیری», «بازبرنامه‌ریزی» and «بررسی تخصیص» (docs/tasks/22-project-dashboard.md).

export function HelpTip({text}) {
    return (
        <span className="pd-help">
            <button type="button" aria-label={text}>؟</button>
            <span className="pd-tip" role="tooltip">{text}</span>
        </span>
    );
}

const memberName = (membership) => {
    const member = membership?.member || {};
    return member.fullname || [member.firstname, member.lastname].filter(Boolean).join(" ") || member.username || "";
};
export const memberOptions = (memberships) => (memberships || [])
    .filter((membership) => membership && !["removed", "restricted"].includes(membership.memberShipStatus))
    .map((membership) => ({value: membership.documentId || membership.id, label: memberName(membership), membership}))
    .filter((option) => option.label);

const drawerPt = {root: {style: {width: "min(36rem, 100vw)"}}, content: {className: "p-0"}};
const STEP_TONES = ["#ef4444", "#f59e0b", "#f97316", "#a855f7", "#3b82f6"];
const EVIDENCE_TONES = ["", "tone-amber", "tone-orange", "tone-orange", "tone-orange"];

// ---------- «چرا در خطر است؟» ----------
export function RiskReasonDrawer({item, visible, onHide, canAct, onFollowUp, onReview, onOpen}) {
    if (!item) return null;
    const risk = item.risk || {};
    const signals = [...(risk.signals || [])].sort((a, b) => b.weight - a.weight);
    const status = RISK_STATUS[risk.status] || RISK_STATUS.needs_attention;
    const subject = item.entityType === "milestone" ? "این مایلستون" : item.entityType === "project" ? "این پروژه" : item.entityType === "member" ? "کارهای این عضو" : "این کار";
    return (
        <Sidebar visible={visible} position="left" onHide={onHide} className="rtl" showCloseIcon pt={drawerPt}
                 header={<div className="flex align-items-center gap-2"><b className="text-sm">{`چرا ${subject} ${status.label} تشخیص داده شده؟`}</b>
                     <HelpTip text="«در خطر» یک تحلیل سیستم است، نه وضعیت قطعی کار. زوفینو فقط داده‌های واقعی ثبت‌شده را بررسی می‌کند و توضیح می‌دهد کدام نشانه‌ها باعث هشدار شده‌اند."/></div>}>
            <div className="p-4 flex flex-column gap-3" dir="rtl">
                <p className="pd-sub m-0">{`«${item.title}» — شواهد را از بالا به پایین ببینید؛ هیچ علت پنهان یا فرضی ساخته نشده است.`}</p>
                <div className="pd-bg tone-amber border-round-2xl p-3 flex gap-2">
                    <Icon icon="mi mi:info" className="tone-amber"/>
                    <div>
                        <b className="text-xs tone-amber">این هشدار یعنی چه؟</b>
                        <p className="pd-tiny m-0 mt-1 line-height-3 text-700">
                            {`${subject === "این کار" ? "کار متوقف نشده است." : ""} زوفینو می‌گوید با توجه به زمان، پیشرفت و ارتباط‌های ثبت‌شده، احتمال دارد ادامه روند فعلی به مشکل منجر شود و بهتر است بررسی شود.`}
                        </p>
                    </div>
                </div>

                {signals.map((signal, index) => {
                    const text = signalText(signal);
                    return (
                        <div key={`${signal.type}-${index}`} className={`pd-evidence ${EVIDENCE_TONES[index] || "tone-orange"}`}>
                            <div className="flex align-items-start gap-3">
                                <span className="pd-step" style={{background: STEP_TONES[index] || STEP_TONES[4]}}>{index + 1}</span>
                                <div className="flex-1">
                                    <span className="pd-tiny font-bold" style={{color: STEP_TONES[index] || STEP_TONES[4]}}>{text.title}</span>
                                    <b className="text-xs block mt-1">{text.text}</b>
                                    {text.detail && <p className="pd-tiny text-600 m-0 mt-2">{text.detail}</p>}
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div className="border-1 border-200 border-round-2xl p-3 surface-50">
                    <span className="pd-tiny text-600 font-bold">نتیجه تحلیل زوفینو</span>
                    <div className="flex align-items-center gap-2 mt-2">
                        <Icon icon={`mi mi:${status.icon}`} className={`tone-${status.tone}`}/>
                        <b className="text-sm">{`${SEVERITY[risk.severity] || status.label} — نیازمند بررسی`}</b>
                    </div>
                    <p className="pd-tiny text-600 m-0 mt-2 line-height-3">
                        {`علت: ${signals.map((signal) => signalText(signal).title).join(" + ")}.`}
                        {" زوفینو ادعا نمی‌کند این مورد به‌تنهایی پروژه را چند روز عقب می‌اندازد."}
                    </p>
                </div>

                <div className="pd-bg tone-blue border-round-2xl p-3">
                    <b className="text-xs tone-blue">آیا واقعاً مشکلی وجود دارد؟</b>
                    <p className="pd-tiny m-0 mt-1 line-height-3 text-700">
                        مدیر یا مسئول کار می‌تواند وضعیت را بررسی کند. اگر همه‌چیز طبق برنامه است، هشدار را «بررسی‌شده» ثبت کنید؛ اگر مشکل واقعی وجود دارد، برای آن پیگیری بسازید.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        {canAct && <button type="button" className="pd-btn primary" onClick={() => onFollowUp(item)}>ایجاد پیگیری</button>}
                        {canAct && <button type="button" className="pd-btn" onClick={() => onReview(item)}>بررسی شد، فعلاً مشکلی نیست</button>}
                        {onOpen && <button type="button" className="pd-btn" onClick={() => onOpen(item)}>باز کردن</button>}
                    </div>
                    {!canAct && <p className="pd-tiny text-500 m-0 mt-2">ثبت پیگیری یا بررسی برای مدیر پروژه یا مسئول کار است.</p>}
                </div>
            </div>
        </Sidebar>
    );
}

// ---------- «نیازمند اقدام»: every attention item ----------
export function AttentionDrawer({visible, onHide, items, reviewedCount, onWhy, onFollowUp, canActOn}) {
    return (
        <Sidebar visible={visible} position="left" onHide={onHide} className="rtl" pt={drawerPt}
                 header={<div><b className="text-sm">نیازمند اقدام</b><p className="pd-sub">موارد مرتب‌شده بر اساس شدت نشانه‌ها</p></div>}>
            <div className="p-4 flex flex-column gap-3" dir="rtl">
                {!items.length && <p className="text-xs text-500 text-center p-4">موردی نیازمند توجه نیست.</p>}
                {items.map((item) => {
                    const status = RISK_STATUS[item.risk.status];
                    return (
                        <div key={item.key} className={`pd-attention tone-${status.tone}`}>
                            <span className={`pd-pill tone-${status.tone}`}>{SEVERITY[item.risk.severity]}</span>
                            <h4 className="text-sm font-bold mt-2 mb-0">{attentionTitle(item)}</h4>
                            <p className="text-xs text-600 mt-2 mb-0 line-height-3">{signalsSummary(item.risk.signals)}</p>
                            {item.followUp && <p className="pd-tiny text-500 m-0 mt-2">{`پیگیری: ${item.followUp.owner?.name || ""} تا ${shortDate(item.followUp.dueAt)}`}</p>}
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button type="button" className="pd-btn small" onClick={() => onWhy(item)}>چرا؟</button>
                                {canActOn(item) && !item.followUp && <button type="button" className="pd-btn small" onClick={() => onFollowUp(item)}>پیگیری</button>}
                            </div>
                        </div>
                    );
                })}
                {reviewedCount > 0 && <p className="pd-tiny text-500 m-0">{`${reviewedCount} مورد اخیراً «بررسی‌شده» ثبت شده و تا وقتی بدتر نشود نمایش داده نمی‌شود.`}</p>}
            </div>
        </Sidebar>
    );
}

// ---------- «برنامه زمانی پروژه» ----------
const TIMELINE_TONE = {done: "#10b981", at_risk: "#ef4444", needs_attention: "#f59e0b", future: "#d6d3d1", today: "var(--primary-color)"};

export function TimelineDrawer({visible, onHide, dashboard, onWhyTask, onReschedule, canReschedule}) {
    const project = dashboard?.project || {};
    const prediction = dashboard?.forecast || {};
    const delay = prediction.available ? prediction.delayDays : null;
    return (
        <Sidebar visible={visible} position="left" onHide={onHide} className="rtl" pt={{...drawerPt, root: {style: {width: "min(39rem, 100vw)"}}}}
                 header={<div><div className="flex align-items-center gap-2"><b className="text-sm">برنامه زمانی پروژه</b>
                     {delay > 0 && <span className="pd-pill tone-amber">{`${delay} روز تأخیر پیش‌بینی‌شده`}</span>}</div>
                     <p className="pd-sub">از شروع پروژه تا تحویل؛ همراه با مایلستون‌ها و کارهای در خطر</p></div>}>
            <div className="p-4 flex flex-column gap-4" dir="rtl">
                <div className="grid m-0 gap-2">
                    <div className="col pd-mini"><span className="pd-tiny text-500">شروع پروژه</span><b className="text-xs block mt-1">{fullDate(project.startAt) || "ثبت نشده"}</b></div>
                    <div className="col pd-mini"><span className="pd-tiny text-500">تاریخ برنامه‌ریزی‌شده</span><b className="text-xs block mt-1">{fullDate(project.dueDate) || "ثبت نشده"}</b></div>
                    <div className={`col pd-mini ${prediction.available ? "pd-bg tone-amber" : ""}`}><span className="pd-tiny text-500">پیش‌بینی فعلی تحویل</span>
                        <b className="text-xs block mt-1">{prediction.available ? fullDate(prediction.at) : "داده کافی نیست"}</b></div>
                </div>

                <div className="border-1 border-200 border-round-2xl p-3">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <b className="text-xs">مسیر زمانی</b>
                        <div className="flex gap-3 pd-tiny text-500">
                            <span className="flex align-items-center gap-1"><i className="pd-dot" style={{background: TIMELINE_TONE.done}}/>انجام‌شده</span>
                            <span className="flex align-items-center gap-1"><i className="pd-dot" style={{background: TIMELINE_TONE.at_risk}}/>در خطر</span>
                            <span className="flex align-items-center gap-1"><i className="pd-dot" style={{background: TIMELINE_TONE.future}}/>آینده</span>
                        </div>
                    </div>
                    <div className="pd-timeline">
                        {(dashboard?.timeline || []).map((entry, index) => <TimelineEntry key={`${entry.type}-${entry.documentId || index}`} entry={entry} onWhyTask={onWhyTask}/>)}
                    </div>
                </div>

                <div className={`border-round-2xl p-3 ${prediction.available ? "pd-bg tone-amber" : "surface-50 border-1 border-200"}`}>
                    <div className="flex align-items-start gap-2">
                        <Icon icon="mi mi:schedule" className={prediction.available ? "tone-amber" : "text-500"}/>
                        <div>
                            <b className="text-xs">{prediction.available ? "پیش‌بینی بر چه اساسی است؟" : "چرا پیش‌بینی نشان داده نمی‌شود؟"}</b>
                            <p className="pd-tiny m-0 mt-1 line-height-3 text-700">
                                {prediction.available
                                    ? `بر اساس سرعت ۴ هفته گذشته (${prediction.recentDone} کار تمام‌شده) و کار باقی‌مانده. اعتماد به این تحلیل ${prediction.confidence}٪ است.${delay > 0 ? ` با این روند، تحویل حدود ${delay} روز بعد از موعد پروژه است.` : delay !== null ? " با این روند، پروژه تا موعد تمام می‌شود." : ""}`
                                    : "زوفینو بدون داده کافی تاریخی پیش‌بینی نمی‌کند؛ این بهتر از یک عدد ساختگی است."}
                            </p>
                        </div>
                    </div>
                </div>

                {canReschedule && (dashboard?.overdueTasks || []).length > 0 && (
                    <div className="flex gap-2"><button type="button" className="pd-btn primary" onClick={onReschedule}>بازبرنامه‌ریزی کارهای عقب‌افتاده</button></div>
                )}
            </div>
        </Sidebar>
    );
}

function TimelineEntry({entry, onWhyTask}) {
    const tone = entry.type === "milestone"
        ? (entry.status === "done" ? TIMELINE_TONE.done : entry.risk === "at_risk" ? TIMELINE_TONE.at_risk : entry.risk === "needs_attention" ? TIMELINE_TONE.needs_attention : TIMELINE_TONE.future)
        : entry.type === "risk" ? TIMELINE_TONE.at_risk
            : entry.type === "today" ? TIMELINE_TONE.today
                : entry.type === "start" ? TIMELINE_TONE.done
                    : TIMELINE_TONE.future;
    let body;
    if (entry.type === "start") body = <div><b className="text-xs">شروع پروژه</b><p className="pd-tiny text-500 m-0 mt-1">{fullDate(entry.at)}</p></div>;
    else if (entry.type === "today") body = <div><b className="text-xs" style={{color: "var(--primary-color)"}}>امروز</b><p className="pd-tiny text-500 m-0 mt-1">{fullDate(entry.at)}</p></div>;
    else if (entry.type === "end") body = <div><b className="text-xs">تحویل پروژه (برنامه)</b><p className="pd-tiny text-500 m-0 mt-1">{fullDate(entry.at)}</p></div>;
    else if (entry.type === "forecast") body = <div><b className="text-xs tone-amber">پیش‌بینی تحویل</b><p className="pd-tiny text-500 m-0 mt-1">{fullDate(entry.at)}{entry.delayDays > 0 ? ` · ${entry.delayDays} روز بعد از موعد` : ""}</p></div>;
    else if (entry.type === "risk") body = (
        <div className="pd-bg tone-red border-round-xl p-2">
            <div className="flex justify-content-between gap-2"><b className="text-xs">{`کار در خطر: ${entry.title}`}</b><span className="pd-tiny tone-red font-bold">نشانه ریسک</span></div>
            <p className="pd-tiny text-600 m-0 mt-1">{entry.at ? `موعد: ${shortDate(entry.at)}` : "بدون موعد"}</p>
            <button type="button" className="pd-link mt-2 tone-red" onClick={() => onWhyTask(entry.documentId)}>چرا این مورد در خطر است؟</button>
        </div>
    );
    else body = (
        <div>
            <div className="flex justify-content-between gap-2">
                <b className="text-xs">{`مایلستون: ${entry.title}`}</b>
                {entry.status === "done"
                    ? <span className="pd-pill tone-green">تمام‌شده</span>
                    : entry.risk !== "normal" ? <span className={`pd-pill tone-${RISK_STATUS[entry.risk].tone}`}>{RISK_STATUS[entry.risk].label}</span> : null}
            </div>
            <p className="pd-tiny text-500 m-0 mt-1">
                {entry.status === "done"
                    ? `تحویل‌شده${entry.doneAt ? ` · ${fullDate(entry.doneAt)}` : ""}`
                    : entry.dueAt ? `سررسید (دیرترین موعد کارها): ${fullDate(entry.dueAt)} · ${entry.percent}٪` : `بدون موعد · ${entry.percent}٪`}
            </p>
            {entry.status !== "done" && <div className="pd-bar mt-2"><span style={{width: `${entry.percent}%`}}/></div>}
        </div>
    );
    return (
        <div className="pd-timeline-item">
            <span className="pd-timeline-dot" style={{background: tone}}/>
            {body}
        </div>
    );
}

// ---------- «ایجاد پیگیری» ----------
const REMIND_OPTIONS = [
    {value: 120, label: "۲ ساعت قبل"},
    {value: 60, label: "۱ ساعت قبل"},
    {value: 1440, label: "۱ روز قبل"},
    {value: 0, label: "بدون یادآوری"},
];
const HOUR_OPTIONS = Array.from({length: 14}, (_, index) => ({value: index + 8, label: `ساعت ${index + 8}`}));

export function FollowUpDialog({item, visible, onHide, members, defaultOwner, onSubmit}) {
    const [owner, setOwner] = useState(null);
    const [date, setDate] = useState(null);
    const [hour, setHour] = useState(10);
    const [remind, setRemind] = useState(120);
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const options = useMemo(() => memberOptions(members), [members]);

    useEffect(() => {
        if (!visible) return;
        const tomorrow = new Date(Date.now() + 86400000);
        setOwner(defaultOwner || null);
        setDate(Math.floor(tomorrow.getTime() / 1000));
        setHour(10);
        setRemind(120);
        setNote("");
        setError("");
    }, [visible, defaultOwner]);

    if (!item) return null;
    const submit = async () => {
        if (!owner) return setError("مسئول پیگیری را انتخاب کنید.");
        if (!date) return setError("مهلت پیگیری را انتخاب کنید.");
        const dueAt = new Date(date * 1000);
        dueAt.setHours(hour, 0, 0, 0);
        setSaving(true);
        setError("");
        try {
            await onSubmit({entityType: item.entityType, entityId: item.entityId, entityTitle: item.title, owner, dueAt: dueAt.toISOString(), remindBeforeMinutes: remind, note});
        } catch (requestError) {
            setError("پیگیری ثبت نشد. دوباره تلاش کنید.");
        } finally {
            setSaving(false);
        }
    };
    const first = [...(item.risk?.signals || [])].sort((a, b) => b.weight - a.weight)[0];
    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-6 lg:w-4" modal
                header={<div><b className="text-base">ایجاد پیگیری</b><p className="pd-sub">{`برای «${attentionTitle(item)}»`}</p></div>}
                footer={<div className="flex justify-content-end gap-2">
                    <button type="button" className="pd-btn" onClick={onHide}>انصراف</button>
                    <button type="button" className="pd-btn primary" disabled={saving} onClick={submit}>{saving ? "در حال ثبت…" : "ایجاد پیگیری"}</button>
                </div>}>
            <div className="flex flex-column gap-3" dir="rtl">
                {first && <div className="pd-bg tone-red border-round-xl p-2 text-xs tone-red">{`نشانه اصلی: ${signalText(first).text}`}</div>}
                <p className="pd-tiny text-500 m-0">پیگیری وضعیت کار را تغییر نمی‌دهد؛ فقط مشخص می‌کند چه کسی تا چه زمانی این موضوع را بررسی کند.</p>
                <label className="flex flex-column gap-1 text-xs font-bold text-600">مسئول پیگیری
                    <Dropdown value={owner} options={options} onChange={(event) => setOwner(event.value)} filter placeholder="انتخاب عضو" className="w-full text-xs" panelClassName="rtl text-xs"/>
                </label>
                <div className="grid m-0 gap-2">
                    <label className="col flex flex-column gap-1 text-xs font-bold text-600 p-0">مهلت پیگیری
                        <PrimeDatePicker type="PERSIAN_DATE" inputClassName="w-full text-xs" placeholder="تاریخ" value={date} onChange={(event) => setDate(event.value)}/>
                    </label>
                    <label className="col flex flex-column gap-1 text-xs font-bold text-600 p-0">ساعت
                        <Dropdown value={hour} options={HOUR_OPTIONS} onChange={(event) => setHour(event.value)} className="w-full text-xs" panelClassName="rtl text-xs"/>
                    </label>
                    <label className="col flex flex-column gap-1 text-xs font-bold text-600 p-0">یادآوری
                        <Dropdown value={remind} options={REMIND_OPTIONS} onChange={(event) => setRemind(event.value)} className="w-full text-xs" panelClassName="rtl text-xs"/>
                    </label>
                </div>
                <label className="flex flex-column gap-1 text-xs font-bold text-600">یادداشت
                    <InputTextarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="مثلاً با تأمین‌کننده تا فردا پیگیری شود…" className="w-full text-xs" autoResize/>
                </label>
                {error && <span className="text-xs tone-red">{error}</span>}
            </div>
        </Dialog>
    );
}

// ---------- «بازبرنامه‌ریزی کارهای عقب‌افتاده» ----------
export function RescheduleDialog({visible, onHide, tasks, members, onSave}) {
    const [changes, setChanges] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const options = useMemo(() => memberOptions(members), [members]);
    useEffect(() => {
        if (visible) {
            setChanges({});
            setError("");
        }
    }, [visible]);
    const change = (task, field, value) => setChanges((current) => ({...current, [task.documentId]: {...(current[task.documentId] || {}), [field]: value}}));
    const now = Date.now();
    const stillLate = tasks.filter((task) => {
        const dueUnix = changes[task.documentId]?.dueDate;
        return !dueUnix || dueUnix * 1000 < now;
    }).length;
    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await onSave(tasks.filter((task) => changes[task.documentId]).map((task) => ({task, change: changes[task.documentId]})));
        } catch (requestError) {
            setError("بعضی تغییرات ذخیره نشد. دوباره تلاش کنید.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-8 lg:w-6" modal
                header={<div><b className="text-base">بازبرنامه‌ریزی کارهای عقب‌افتاده</b><p className="pd-sub">تاریخ یا مسئول کارهایی را که از موعد گذشته‌اند تغییر دهید.</p></div>}
                footer={<div className="flex justify-content-end gap-2">
                    <button type="button" className="pd-btn" onClick={onHide}>انصراف</button>
                    <button type="button" className="pd-btn primary" disabled={saving || !Object.keys(changes).length} onClick={save}>{saving ? "در حال ذخیره…" : "اعمال تغییرات"}</button>
                </div>}>
            <div className="flex flex-column gap-2" dir="rtl">
                <div className="grid m-0 pd-tiny text-500 px-2"><span className="col-6 p-0">کار</span><span className="col-3 p-0">تاریخ جدید</span><span className="col-3 p-0">مسئول</span></div>
                {tasks.map((task) => {
                    const lateDays = task.dueDate ? Math.max(1, Math.floor((now - new Date(task.dueDate).getTime()) / 86400000)) : 0;
                    return (
                        <div key={task.documentId} className="grid m-0 align-items-center border-1 border-200 border-round-xl p-2 gap-0">
                            <div className="col-6 p-1"><b className="text-xs">{task.title}</b><p className="pd-tiny tone-red m-0">{`${lateDays} روز عقب‌افتاده`}</p></div>
                            <div className="col-3 p-1"><PrimeDatePicker type="PERSIAN_DATE" inputClassName="w-full text-xs" placeholder="تاریخ" value={changes[task.documentId]?.dueDate || null} onChange={(event) => change(task, "dueDate", event.value)}/></div>
                            <div className="col-3 p-1"><Dropdown value={changes[task.documentId]?.assignee ?? task.assignee?.documentId ?? null} options={options} onChange={(event) => change(task, "assignee", event.value)} className="w-full text-xs" panelClassName="rtl text-xs" placeholder="مسئول"/></div>
                        </div>
                    );
                })}
                <div className="pd-bg tone-amber border-round-xl p-2 pd-tiny tone-amber">
                    {`پیش‌نمایش: پس از ذخیره ${stillLate ? `${stillLate} کار همچنان عقب‌افتاده می‌ماند` : "کار عقب‌افتاده‌ای باقی نمی‌ماند"}. اثر روی پیش‌بینی پس از ذخیره دوباره محاسبه می‌شود.`}
                </div>
                {error && <span className="text-xs tone-red">{error}</span>}
            </div>
        </Dialog>
    );
}

// ---------- «بررسی تخصیص و ظرفیت» ----------
export function AllocationDialog({visible, onHide, team, focus, openTasks, members, onSave}) {
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const options = useMemo(() => memberOptions(members), [members]);
    useEffect(() => {
        if (visible) {
            setFrom(focus || team[0]?.person?.documentId || null);
            setTo(null);
            setSelected([]);
            setError("");
        }
    }, [visible, focus, team]);
    const ownTasks = openTasks.filter((task) => task.assignee?.documentId === from);
    const save = async () => {
        if (!to || !selected.length) return;
        setSaving(true);
        setError("");
        try {
            await onSave(ownTasks.filter((task) => selected.includes(task.documentId)), to);
        } catch (requestError) {
            setError("جابه‌جایی ذخیره نشد. دوباره تلاش کنید.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-8 lg:w-6" modal
                header={<div><b className="text-base">بررسی تخصیص و ظرفیت</b><p className="pd-sub">کارهای یک عضو را با وضعیت بقیه مقایسه کنید. هیچ تخصیصی بدون تأیید شما تغییر نمی‌کند.</p></div>}
                footer={<div className="flex justify-content-end gap-2">
                    <button type="button" className="pd-btn" onClick={onHide}>انصراف</button>
                    <button type="button" className="pd-btn primary" disabled={saving || !to || !selected.length} onClick={save}>{saving ? "در حال ذخیره…" : "اعمال جابه‌جایی"}</button>
                </div>}>
            <div className="flex flex-column gap-3" dir="rtl">
                <div className="grid m-0 gap-2">
                    {team.map((row) => (
                        <button type="button" key={row.person?.documentId} onClick={() => { setFrom(row.person?.documentId); setSelected([]); }}
                                className={`col pd-mini text-right border-1 cursor-pointer ${from === row.person?.documentId ? "border-primary" : "border-transparent"} ${row.load > 100 || row.overdue >= 3 ? "tone-red" : ""}`} style={{minWidth: "8rem", fontFamily: "inherit"}}>
                            <span className="pd-tiny text-600">{row.person?.name}</span>
                            <b className="block text-base mt-1">{row.load !== null ? `${row.load}٪` : `${row.open} کار باز`}</b>
                            <span className="pd-tiny text-500">{row.load !== null ? `${row.open} کار باز · ` : ""}{`${row.overdue} عقب‌افتاده · ${row.dueThisWeek} موعد این هفته`}</span>
                        </button>
                    ))}
                </div>
                {team.every((row) => row.load === null) && <p className="pd-tiny text-500 m-0">برای این اعضا ظرفیت ثبت نشده؛ به جای درصد ظرفیت، تعداد کارها نمایش داده می‌شود.</p>}
                <span className="text-xs font-bold text-600">کارهای باز این عضو</span>
                {ownTasks.map((task) => (
                    <label key={task.documentId} className="flex align-items-center justify-content-between gap-2 border-1 border-200 border-round-xl p-2 cursor-pointer">
                        <span><b className="text-xs">{task.title}</b><span className="pd-tiny text-500 block mt-1">{task.dueDate ? `موعد: ${shortDate(task.dueDate)}` : "بدون موعد"}{task.risk?.status !== "normal" ? ` · ${RISK_STATUS[task.risk.status].label}` : ""}</span></span>
                        <Checkbox checked={selected.includes(task.documentId)} onChange={(event) => setSelected((current) => event.checked ? [...current, task.documentId] : current.filter((id) => id !== task.documentId))}/>
                    </label>
                ))}
                {!ownTasks.length && <p className="text-xs text-500 m-0">کار بازی ندارد.</p>}
                <label className="flex flex-column gap-1 text-xs font-bold text-600">واگذاری کارهای انتخاب‌شده به
                    <Dropdown value={to} options={options.filter((option) => option.value !== from)} onChange={(event) => setTo(event.value)} filter placeholder="انتخاب عضو" className="w-full text-xs" panelClassName="rtl text-xs"/>
                </label>
                {error && <span className="text-xs tone-red">{error}</span>}
            </div>
        </Dialog>
    );
}

export {daysText, TASK_STATUS};
